import { InstagramLogo, TiktokLogo, ArrowUpRight } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import useSiteSettings from "@/hooks/useSiteSettings";

// 7 tiles in a bento layout. Order matters — slots 0,3,6 are bigger.
const TILE_SPANS = ["row-span-2", "", "", "col-span-2", "", "", "col-span-2"];

const DEFAULT_SHOTS = [
    "https://images.unsplash.com/photo-1619926096619-5956ab4dfb1b?crop=entropy&cs=srgb&fm=jpg&w=900",
    "https://images.pexels.com/photos/37025257/pexels-photo-37025257.jpeg",
    "https://images.pexels.com/photos/28992999/pexels-photo-28992999.jpeg",
    "https://images.unsplash.com/photo-1539252554453-80ab65ce3586?crop=entropy&cs=srgb&fm=jpg&w=900",
    "https://images.pexels.com/photos/19202829/pexels-photo-19202829.jpeg",
    "https://images.unsplash.com/photo-1624128082323-beb6b8b508db?crop=entropy&cs=srgb&fm=jpg&w=900",
    "https://images.pexels.com/photos/15337739/pexels-photo-15337739.jpeg",
];

export default function Gallery() {
    const { t } = useI18n();
    const { settings } = useSiteSettings();

    // Owner-controlled images take priority. Any empty slot falls back to a
    // default so the layout never breaks.
    const urls = TILE_SPANS.map(
        (_, i) => settings.gallery_images?.[i] || DEFAULT_SHOTS[i],
    );

    return (
        <section id="gallery" data-testid="gallery" className="bg-background py-20 md:py-28">
            <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">@jandw_deli</p>
                        <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl mt-2">
                            {t("gallery.title")}
                        </h2>
                        <p className="mt-3 text-muted-foreground max-w-xl">{t("gallery.subtitle")}</p>
                    </div>
                    <div className="flex gap-3">
                        <a
                            href="https://instagram.com"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="follow-instagram"
                            className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-5 py-3 font-bold uppercase tracking-wider text-sm hover:-translate-y-0.5 transition"
                        >
                            <InstagramLogo size={16} weight="bold" />
                            Follow
                            <ArrowUpRight size={14} weight="bold" />
                        </a>
                        <a
                            href="https://tiktok.com"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="follow-tiktok"
                            className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground rounded-full px-5 py-3 font-bold uppercase tracking-wider text-sm hover:-translate-y-0.5 transition"
                        >
                            <TiktokLogo size={16} weight="bold" />
                            TikTok
                        </a>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
                    {urls.map((url, i) => (
                        <a
                            key={i}
                            href="https://instagram.com"
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`gallery-tile-${i}`}
                            className={`relative overflow-hidden rounded-2xl group ${TILE_SPANS[i]}`}
                        >
                            <img
                                src={url}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                                <InstagramLogo size={36} weight="bold" className="text-white" />
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}
